import React, { useState, useMemo } from "react";
import { ComplianceCertificate } from "../../../types/api";
import DocumentViewer from "../../../components/DocumentViewer/DocumentViewer";
import PdfViewer from "../../../components/pdf/PdfViewer";
import { XIcon } from "lucide-react";
import { S3TempUrl } from "../../../utils/s3Client";

interface ViewCertificateProps {
  certificate: ComplianceCertificate;
  onClose: () => void;
}

const ViewCertificate: React.FC<ViewCertificateProps> = ({
  certificate,
  onClose,
}) => {
  const [viewingDoc, setViewingDoc] = useState<boolean>(false);
  const [signedDocUrl, setSignedDocUrl] = useState<string>("");
  const [loadingDoc, setLoadingDoc] = useState<boolean>(false);
  const { getsignedurl } = useMemo(() => S3TempUrl(), []);

  const getFileExtension = (url: string): string => {
    return url.split(".").pop()?.toLowerCase() || "";
  };
  
  const getFileName = (url: string): string => {
    if (!url) return "";
    try {
      // Handle cases where URL might be encoded
      const decodedUrl = decodeURIComponent(url);
      return decodedUrl.split('/').pop() || "";
    } catch (e) {
      return url.split('/').pop() || "";
    }
  };

  const handleViewDocument = async () => {
    if (!certificate.documentUrl) return;

    setLoadingDoc(true);
    setViewingDoc(true);
    
    try {
      const fileName = getFileName(certificate.documentUrl);
      const fileExt = getFileExtension(fileName);
      
      const signedUrl = await getsignedurl(fileName, fileExt);
      setSignedDocUrl(signedUrl);
    } catch (error) {
      console.error("Error signing URL:", error);
      setSignedDocUrl(certificate.documentUrl);
    } finally {
      setLoadingDoc(false);
    }
  };

  const fileType = certificate.documentUrl ? getFileExtension(certificate.documentUrl) : "";
  const displayFileName = certificate.documentUrl ? getFileName(certificate.documentUrl) : "Document";

  return (
    <div className="flex flex-col max-h-[85vh] h-full">
      {/* Header */}
      <div className="flex-none px-6 pt-6">
        <h2 className="text-2xl font-semibold">View Certificate</h2>
        <hr className="mt-4 border-t-2 border-gray-300" />
      </div>

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto px-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <label className="block mb-1 font-semibold text-black text-[1rem]">
              Asset Name
            </label>
            <div>
              <p className="mt-1 text-sm text-blue-900 font-bold">
                {certificate.assetName || "-"}
              </p>
            </div>
          </div>
          <div>
            <label className="block mb-1 font-semibold text-black text-[1rem]">
              Asset ID
            </label>
            <div>
              <p className="mt-1 text-sm text-blue-900 font-bold">
                {certificate.assetId || "-"}
              </p>
            </div>
          </div>
          <div>
            <label className="block mb-1 font-semibold text-black text-[1rem]">
              Certificate Type
            </label>
            <div>
              <p className="mt-1 text-sm text-blue-900 font-bold">
                {certificate.certificateType || "-"}
              </p>
            </div>
          </div>
          <div>
            <label className="block mb-1 font-semibold text-black text-[1rem]">
              Status
            </label>
            <div>
              <span
                className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                  certificate.status === "Valid"
                    ? "bg-green-100 text-green-800"
                    : certificate.status === "Expiring Soon"
                    ? "bg-yellow-100 text-yellow-800"
                    : "bg-red-100 text-red-800"
                }`}
              >
                {certificate.status}
              </span>
            </div>
          </div>
          <div>
            <label className="block mb-1 font-semibold text-black text-[1rem]">
              Issue Date
            </label>
            <div>
              <p className="mt-1 text-sm text-blue-900 font-bold">
                {certificate.issueDate || "-"}
              </p>
            </div>
          </div>
          <div>
            <label className="block mb-1 font-semibold text-black text-[1rem]">
              Expiry Date
            </label>
            <div>
              <p className="mt-1 text-sm text-blue-900 font-bold">
                {certificate.expiryDate || "-"}
              </p>
            </div>
          </div>
          
          <div className="md:col-span-2">
            <label className="block mb-1 font-semibold text-black text-[1rem]">
              Certificate Document
            </label>
            <div className="mt-1">
              {certificate.documentUrl ? (
                <div className="bg-white shadow-md rounded-lg overflow-hidden max-w-md">
                  <table className="w-full text-left border-collapse">
                    <thead className="bg-gray-100 text-gray-600 uppercase text-sm">
                      <tr>
                        <th className="p-2">File</th>
                        <th className="p-2">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-t">
                        <td className="p-2 truncate max-w-[200px]" title={displayFileName}>
                          {displayFileName}
                        </td>
                        <td className="p-2">
                          <button
                            className="text-blue-500 hover:underline"
                            onClick={handleViewDocument}
                          >
                            View
                          </button>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="mt-1 text-sm text-gray-600">
                  No document attached
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Document Viewer Modal Overlay */}
      {viewingDoc && certificate.documentUrl && (
        <div
          className="fixed inset-0 z-[60]"
          style={{
            backgroundColor: "rgba(0,0,0,0.5)",
          }}
        >
          <XIcon
            className="absolute right-4 top-4 z-10 cursor-pointer bg-red-500 text-white p-1 rounded"
            size={24}
            onClick={() => {
              setViewingDoc(false);
              setSignedDocUrl("");
            }}
          />
          <div
            className="absolute bg-white border p-4"
            style={{
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: "calc(100vw - 200px)",
              height: "calc(100vh - 100px)",
              overflow: "auto",
              boxSizing: "border-box",
            }}
          >
            {loadingDoc ? (
              <div className="flex justify-center items-center h-full">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
              </div>
            ) : (
              fileType === "pdf" ? (
                <PdfViewer fileUrl={signedDocUrl || certificate.documentUrl} />
              ) : (
                <DocumentViewer file={signedDocUrl || certificate.documentUrl} name={displayFileName} />
              )
            )}
          </div>
        </div>
      )}

      {/* Footer Actions */}
      <div className="flex-none py-4 px-6 bg-gray-600 flex justify-end gap-4 rounded-b-xl border-t">
        <button
          type="button"
          className="bg-gray-300 text-gray-800 px-6 py-2 rounded-full font-medium hover:bg-gray-400 transition-colors"
          onClick={onClose}
        >
          Close
        </button>
      </div>
    </div>
  );
};

export default ViewCertificate;